import {
  BugFilled,
  CheckOutlined,
  CloseOutlined,
  DatabaseFilled,
  DatabaseOutlined,
  DownloadOutlined,
  LoadingOutlined,
  LockFilled,
  LoginOutlined,
  ReloadOutlined,
  UnlockFilled,
} from '@ant-design/icons';
import { Alert, App, Button, Card, Checkbox, Col, Divider, Form, Input, Row, Segmented, Select, Spin, Tag, theme, Tooltip, Typography } from 'antd';
import { RuleObject } from 'antd/es/form';
import i18next from 'i18next';
import moment from 'moment';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectCountry from 'src/common/components/business/country-select';
import LanguageSelect from 'src/common/components/business/language-select';
import { getDateFormat } from 'src/common/utils';
import { formatMessage } from 'src/core/services/http.service';
import { nameof } from 'src/core/utils/object';
import { AppConfig, container } from 'src/inversify.config';
import {
  getDefaultOrganizationTestingEndDate,
  OrganizationCreationStatus,
  OrganizationImportStore,
  OrganizationItem,
  OrganizationItemStore,
  OrganizationSummaryStore,
} from 'src/stores/organizations-store';
import { OrganizationExportImportContext, OrganizationExportImportContextAction, OrganizationExportState } from '../organization-export-context';
import { curateString } from 'src/core/utils/object';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { ENV } from 'src';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { guid } from 'odata-query';
import { CommandResult } from 'src/core/stores/types';
import { useIdentity } from 'src/core/services/authentication';
import { PermissionsConstants } from 'src/common/types';

const { Text } = Typography;

const loadingIcon = <LoadingOutlined style={{ fontSize: 24, color: 'gray' }} spin />;

const langs: { [key: string]: string } = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  ru: 'Russian',
  pt: 'Portuguese',
};

const { useToken } = theme;

const OrganizationGeneralPropertyEditCard: FC<{
  organizationId: string;
  canEditOrg: boolean;
  canBlockOrg: boolean;
  canCreateOrg: boolean;
  canCreatePartnerOrg: boolean;
  canDeleteOrg: boolean;
  canManageAdminOrg: boolean;
  organizationDisplayName: string;
  canViewAllOrganizations: boolean;
}> = ({
  organizationId,
  canEditOrg,
  canViewAllOrganizations,
  canBlockOrg,
  canDeleteOrg,
  canCreateOrg,
  canCreatePartnerOrg,
  organizationDisplayName,
  canManageAdminOrg,
}) => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const [form] = Form.useForm();
  const organizationStore = container.get(OrganizationItemStore);
  const organizationSummaryStore = container.get(OrganizationSummaryStore);
  const organizationState = organizationStore.state;
  const organizationImportStore = container.get(OrganizationImportStore);
  const [error, setError] = useState<string | undefined>('');
  const { organizationExportState, organizationExportDispatch } = useContext(OrganizationExportImportContext);
  const [isExporting, setIsExporting] = useState(organizationExportState.exportState.get(organizationId)?.state === OrganizationExportState.Busy);
  const [fixInconsistenciesDisabled, setFixInconsistenciesDisabled] = useState(false);
  const { modal, notification } = App.useApp();

  const [partnerOptions, setPartnerOptions] = useState<{ label: string; value: string; key: string; isTestingOrg: boolean }[]>([]);

  const { TextArea } = Input;

  const [minChildrenDeleteDate, setMinChildrenDeleteDate] = useState<moment.Moment>();
  const [allChildrenAreTestingOrg, setAllChildrenAreTestingOrg] = useState(true);
  const [hasChildrenOrgs, setHasChildrenOrgs] = useState(false);
  const [parentOrgData, setParentOrgData] = useState<OrganizationItem>();
  const originalParentOrgData = useRef<OrganizationItem>();
  const { token } = useToken();
  const [isTestOrg, setIsTestOrg] = useState<boolean>(false);
  const [isPartnerOrg, setIsPartnerOrg] = useState<boolean>(false);

  const firstLoad = useRef(false);
  const preventRefresh = useRef<boolean>(false);
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(false);

  const identity = useIdentity();
  const isMainOrg = organizationId === identity.profile['tenant_id'];

  const previouslySelectedParentOrg = useRef<OrganizationItem>();

  useEffect(() => {
    console.log(identity);
    if (isExporting) {
      waitForExport();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadParentOptions = (isTestingOrg?: boolean) => {
    organizationSummaryStore
      .getUserPartnerOrganizations(PermissionsConstants.ManagePartnerOrganization, {
        skip: 0,
        take: 1000000,
        filter: [{ isPartner: true, isTestingOrg: isTestingOrg ? undefined : false }, `id ne ${organizationId}`],
      })
      .then((items) => {
        preventRefresh.current = true;
        let newPartnerOptions = items?.map((v) => ({ key: v.id, label: v.displayName, value: v.id, isTestingOrg: v.isTestingOrg })) ?? [];
        if (
          !newPartnerOptions.some((v) => v.value === organizationState?.item?.value.parentOrganizationId) &&
          organizationState?.item?.value?.parentOrganizationId &&
          organizationState?.item?.value.parentOrganizationDisplayName
        ) {
          if ((originalParentOrgData.current?.isTestingOrg && isTestingOrg) || !originalParentOrgData.current?.isTestingOrg) {
            newPartnerOptions.push({
              label: organizationState?.item?.value.parentOrganizationDisplayName,
              key: organizationState?.item?.value.parentOrganizationId,
              value: organizationState?.item?.value.parentOrganizationId,
              isTestingOrg: false,
            });
          }
        }
        setPartnerOptions(newPartnerOptions);
      });
  };

  const loadParentData = async (parentId?: string, initialLoad?: boolean) => {
    if (organizationState?.item?.value?.id === organizationId && (parentId || organizationState?.item?.value?.parentOrganizationId)) {
      const data = await organizationStore.getOrganizationData((parentId || organizationState.item.value.parentOrganizationId)!);
      preventRefresh.current = true;
      if (data?.isTestingOrg && moment(data.testingEndDate).isBefore(form.getFieldValue('testingEndDate'))) {
        modal.warning({ title: t('Warning'), content: t('TestingEndDateAutomaticallyChangedWarning') });
        form.setFieldValue('testingEndDate', moment(data.testingEndDate));
      }
      setParentOrgData(data);
      if (initialLoad) {
        originalParentOrgData.current = data;
      }
      previouslySelectedParentOrg.current = data;
      form.setFieldValue('parentOrganization', parentId || organizationState?.item?.value?.parentOrganizationId);
    } else if (organizationState?.item?.value?.id === organizationId && !(parentId || organizationState.item.value.parentOrganizationId)) {
      setParentOrgData(undefined);
      form.setFieldValue('parentOrganization', undefined);
    }
  };

  const loadChildrenData = async () => {
    if (organizationState?.item?.value?.id === organizationId && organizationState?.item?.value?.isPartner) {
      const data = await organizationSummaryStore.getItems({
        skip: 0,
        take: 1000000,
        filter: { ParentOrganizationId: guid(organizationId) },
      });
      setHasChildrenOrgs(data?.length !== undefined && data.length > 0);
      const childrenAreTestingOrg = data?.every((v) => v.isTestingOrg) ?? true;
      setAllChildrenAreTestingOrg(childrenAreTestingOrg);
      const testingDates = data?.flatMap((v) => (v.isTestingOrg ? [moment(v.testingEndDate)] : [])) ?? [];
      if (testingDates.length > 0) {
        let lastDate = moment.max(testingDates);
        setMinChildrenDeleteDate(lastDate);
      }
    }
  };

  useEffect(() => {
    if (preventRefresh.current) {
      preventRefresh.current = false;
      return;
    }

    if (organizationState?.item?.value?.id === organizationId && !firstLoad.current) {
      setMfaEnabled(organizationState?.item?.value?.twoFactorEnabled ?? false);
      setIsTestOrg(organizationState.item.value.isTestingOrg);
      setIsPartnerOrg(organizationState.item.value?.isPartner);

      firstLoad.current = true;
      loadParentData(undefined, true);
      loadChildrenData();
      loadParentOptions(organizationState.item.value.isTestingOrg);
    }
    form.resetFields();
  }, [organizationState.item.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const waitForExport = async () => {
    await organizationExportState.exportState.get(organizationId)?.promise;
    setIsExporting(false);
  };

  const onSave = async () => {
    if (!canEditOrg) return;

    setError('');
    let item = await form.validateFields();
    item.displayName = curateString(item.displayName);
    item.address = curateString(item.address);
    item.isTestingOrg = isTestOrg;
    item.testingEndDate = isTestOrg ? item.testingEndDate : null;
    const response = await organizationStore.save(organizationId, item as OrganizationItem);
    let changeParentResponse: Partial<CommandResult<{ parentOrganizationId: boolean }>> | undefined = { isSuccess: true };
    let changePartnerStatusResponse: Partial<CommandResult<{ parentOrganizationId: boolean }>> | undefined = { isSuccess: true };

    if (isPartnerOrg !== organizationState.item.value?.isPartner && isPartnerOrg !== undefined) {
      changePartnerStatusResponse = await organizationStore.changePartnerStatus(organizationId, isPartnerOrg);
    }

    if (item?.parentOrganization && item.parentOrganization !== organizationState.item.value?.parentOrganizationId && !item.isPartner) {
      changeParentResponse = await organizationStore.changeParent(organizationId, item.parentOrganization);
    }

    if (response?.isSuccess && changeParentResponse?.isSuccess && changePartnerStatusResponse?.isSuccess) {
      organizationState.set((s) => {
        s.item.displayName = item.displayName;
        s.item.email = item.email;
        s.item.address = item.address;
        s.item.defaultLanguage = item.defaultLanguage;
        s.item.country = item.country;
        s.item.isTestingOrg = isTestOrg;
        s.item.isPartner = isPartnerOrg;
        s.item.parentOrganizationId = parentOrgData?.id;
        s.item.parentOrganizationDisplayName = parentOrgData?.displayName;
        s.item.testingEndDate = isTestOrg ? item.testingEndDate : undefined;
        return s;
      });
      form.resetFields();
    } else setError(formatMessage(response));
  };

  const onReset = () => {
    setError('');
    setIsTestOrg(organizationState.item.value.isTestingOrg);
    setIsPartnerOrg(organizationState.item.value?.isPartner);
    loadParentData();
    loadParentOptions(organizationState.item.value.isTestingOrg);
    form.resetFields();
  };

  const canEnsureDb =
    organizationState.item.value?.creationStatus === OrganizationCreationStatus.Error &&
    ((organizationState.item.value?.isPartner && canCreatePartnerOrg) || (!organizationState.item.value?.isPartner && canCreateOrg));

  const onEnqueueJob = async () => {
    if (!canEnsureDb) return;

    const response = await organizationStore.restartOrganizationCreationFlow(organizationId);
    if (response?.isSuccess) {
      modal.success({
        title: t('Creation Job for this organization has been enqueued'),
        okText: t('OK'),
      });
    } else {
      modal.error({
        title: t('SomethingWentWrong'),
        content: formatMessage(response),
        okText: t('OK'),
      });
    }
  };

  const onChangeBlockStatus = async () => {
    if (!canBlockOrg) return;

    const nextValue = !organizationState.item.value?.isBlocked;
    const response = await organizationStore.changeOrganizationBlockStatus(organizationId, nextValue);
    if (response && response.isSuccess) {
      organizationState.set((s) => {
        s.item.isBlocked = nextValue;
        return s;
      });
      modal.success({
        title: nextValue ? t('OrganizationHasBeenBlocked') : t('OrganizationHasBeenUnblocked'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    } else {
      modal.error({
        title: t('SomethingWentWrong'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    }
  };

  const onCheckOrganizationDatabase = async () => {
    if (!canCreateOrg && !canCreatePartnerOrg) return;

    const response = await organizationStore.checkOrganizationDatabase(organizationId);
    if (response && response.isSuccess) {
      modal.success({
        title: t('Migrations are Ok'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    } else {
      modal.error({
        title: t('Migrations failed'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
    }
  };

  const onFixInconsistencies = async () => {
    setFixInconsistenciesDisabled(true);
    const orgStatus = organizationState?.item?.value?.creationStatus;
    if (orgStatus !== OrganizationCreationStatus.Available) return;

    const organizationIsLocked = await organizationStore.checkLockedStatus(organizationId);

    if (organizationIsLocked) {
      notification.error({
        placement: 'bottomRight',
        duration: 20,
        style: {
          width: 600,
        },
        message: t('OrganizationIsLocked'),
      });
      return;
    }

    await organizationStore.fixScopesSyncIssues(organizationId);
    notification.success({
      placement: 'bottomRight',
      duration: 20,
      style: {
        width: 600,
      },
      message: t('InconsistencyFixStartedMessage'),
    });
  };

  let isPartnerValue = isPartnerOrg ? t('Yes') : t('No');
  let isBlockedValue = organizationState.item.value?.isBlocked ? t('Yes') : t('No');

  const onExport = async () => {
    const organizationIsLocked = await organizationStore.checkLockedStatus(organizationId);

    if (organizationIsLocked) {
      notification.error({
        placement: 'bottomRight',
        duration: 20,
        style: {
          width: 600,
        },
        message: t('OrganizationIsLocked'),
      });
      return;
    }

    const exportingPromise = organizationImportStore.exportFile(
      organizationState.item.id.value,
      `${organizationState.item.displayName.value}_backup.db`
    );

    setIsExporting(true);

    organizationExportDispatch({
      type: OrganizationExportImportContextAction.UpdateExportState,
      organizationId: organizationId,
      state: OrganizationExportState.Busy,
      promise: exportingPromise,
    });

    const result = await exportingPromise;

    if (result && result.isSuccess) {
      notification.success({
        message: t('ExportingOrganizationSuccess'),
        placement: 'bottomRight',
      });
    } else {
      notification.error({
        message: (
          <>
            <span>{t('ErrorExportingOrganization').replace('{0}', organizationDisplayName)}</span>
            {config.environment === ENV.DEV && (
              <>
                <br />
                <br />
                <span>{`${result?.error}`}</span>
              </>
            )}
          </>
        ),
        placement: 'bottomRight',
      });
    }

    organizationExportDispatch({
      type: OrganizationExportImportContextAction.UpdateExportState,
      state: OrganizationExportState.Available,
      organizationId: organizationId,
      promise: exportingPromise,
    });
    setIsExporting(false);
  };

  return (
    <Card
      title={<h3 style={{ textAlign: 'left', marginLeft: 16, textWrap: 'wrap' }}>{t('GeneralProperties')}</h3>}
      extra={
        organizationState?.item?.value?.creationStatus != null ? (
          <>
            <Tooltip title={organizationState.item.value.creationStatusDescription}>
              <Tag>{organizationState.item.value.creationStatus}</Tag>
            </Tooltip>
            {organizationState?.item?.value?.creationStatus != null &&
              organizationState?.item?.value?.creationStatus !== OrganizationCreationStatus.Error &&
              organizationState?.item?.value?.creationStatus !== OrganizationCreationStatus.Available && (
                <ReloadOutlined onClick={() => organizationStore.load(organizationId)} />
              )}
          </>
        ) : null
      }
    >
      {(error || '').length !== 0 && <Alert type="error" message={t(error || '')} />}
      <Form form={form} layout="vertical" autoComplete="off" style={{ margin: '0 20px 20px 20px' }}>
        {canEditOrg ? (
          <>
            <Form.Item
              initialValue={organizationState.item.value?.displayName}
              name={nameof<OrganizationItem>('displayName')}
              label={t('DisplayName')}
              rules={[
                { required: true, message: t('PleaseEnterName') },
                () => ({
                  validator: (ruleObj: RuleObject, value: string) => {
                    const maxLenght = 128;
                    if (value === undefined) return Promise.resolve();
                    if (value.length > maxLenght) {
                      return Promise.reject(t('TextFieldLimitError').replace('{0}', t('DisplayName')).replace('{1}', `${maxLenght}`));
                    } else return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input autoComplete="off" allowClear placeholder={t('DisplayName')} />
            </Form.Item>
            <Form.Item
              initialValue={organizationState.item.value?.email}
              name={nameof<OrganizationItem>('email')}
              label={t('Email')}
              rules={[
                { required: true, type: 'email', message: t('PleaseEnterAValidEmail') },
                () => ({
                  validator: (ruleObj: RuleObject, value: string) => {
                    const maxLenght = 256;
                    if (value === undefined) return Promise.resolve();
                    if (value.length > maxLenght) {
                      return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Email')).replace('{1}', `${maxLenght}`));
                    } else return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input autoComplete="off" allowClear placeholder={t('Email')} />
            </Form.Item>
            <Form.Item
              initialValue={organizationState.item.value?.address}
              name={nameof<OrganizationItem>('address')}
              label={t('Address')}
              rules={[{ required: true, message: t('PleaseEnterAnAddress') }]}
            >
              <TextArea rows={3} autoComplete="off" allowClear placeholder={t('Address')} />
            </Form.Item>
            {canManageAdminOrg ? (
              <Form.Item
                initialValue={organizationState.item.value?.country}
                label={t('Country')}
                name={nameof<OrganizationItem>('country')}
                rules={[{ required: true, message: t('PleaseSelectCountry') }]}
              >
                <SelectCountry />
              </Form.Item>
            ) : (
              <Form.Item>
                <span>
                  {t('Country')}: {organizationState.item.value?.country}
                </span>
              </Form.Item>
            )}

            <Form.Item
              initialValue={organizationState.item.value?.defaultLanguage}
              name="defaultLanguage"
              label={t('DefaultLanguage')}
              rules={[{ required: true, message: t('PleaseSelectALanguage') }]}
            >
              <LanguageSelect />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item label={t('DisplayName')}>
              <span>{organizationState.item.value?.displayName}</span>
            </Form.Item>
            <Form.Item label={t('Email')}>
              <span>{organizationState.item.value?.email}</span>
            </Form.Item>
            <Form.Item label={t('Address')}>
              <span>{organizationState.item.value?.address}</span>
            </Form.Item>
            <Form.Item>
              <span>
                {t('Country')}: {organizationState.item.value?.country}
              </span>
            </Form.Item>
            <Form.Item label={t('DefaultLanguage')}>
              <span>{organizationState.item.value?.defaultLanguage && t(langs[organizationState.item.value.defaultLanguage as string])}</span>
            </Form.Item>
          </>
        )}
        <Row gutter={[16, 8]}>
          <Col lg={12} xs={24}>
            <Form.Item style={{ width: '100%' }} label={t('IsPartner')}>
              {(!canViewAllOrganizations || isMainOrg) && <Input value={isPartnerValue} readOnly autoComplete="off" />}
              {canViewAllOrganizations && !isMainOrg && (
                <>
                  <Segmented
                    disabled={hasChildrenOrgs}
                    className="focused-segmented"
                    block
                    size="large"
                    options={[
                      { label: t('Yes'), value: true },
                      { label: t('No'), value: false },
                    ]}
                    value={isPartnerOrg}
                    onChange={(v) => {
                      preventRefresh.current = true;
                      if (v) {
                        setParentOrgData(undefined);
                        form.setFieldValue('parentOrganization', undefined);
                      } else if (
                        (previouslySelectedParentOrg.current?.isTestingOrg && isTestOrg) ||
                        !previouslySelectedParentOrg.current?.isTestingOrg
                      ) {
                        setParentOrgData(previouslySelectedParentOrg.current);
                        form.setFieldValue('parentOrganization', previouslySelectedParentOrg.current?.id);

                        if (
                          previouslySelectedParentOrg.current?.isTestingOrg &&
                          moment(previouslySelectedParentOrg.current.testingEndDate).isBefore(form.getFieldValue('testingEndDate'))
                        ) {
                          modal.warning({ title: t('Warning'), content: t('TestingEndDateAutomaticallyChangedWarning') });
                          form.setFieldValue('testingEndDate', moment(previouslySelectedParentOrg.current.testingEndDate));
                        }
                      }
                      setIsPartnerOrg(() => v);
                    }}
                  />
                  {hasChildrenOrgs && <Text style={{ color: token.colorError }}>{t('OrgCannotChangePartnerStatusWarning')}</Text>}
                </>
              )}
            </Form.Item>
          </Col>
          <Col lg={12} xs={24}>
            <Form.Item style={{ width: '100%' }} label={t('IsBlocked')}>
              <Input value={isBlockedValue} readOnly autoComplete="off" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 8]}>
          <Col lg={12} xs={24}>
            <Form.Item label={t('PartnerOrganization')}>
              <span>
                {canViewAllOrganizations && !isPartnerOrg && !isMainOrg && (
                  <Form.Item name={'parentOrganization'} initialValue={parentOrgData?.id}>
                    <Select
                      showSearch
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      onChange={(newParent) => {
                        preventRefresh.current = true;
                        loadParentData(newParent);
                        if (newParent.isTestingOrg) {
                          setIsTestOrg(() => true);
                          form.setFieldValue('isTestingOrg', true);
                        }
                      }}
                      placeholder={t('PleaseSelect')}
                      options={partnerOptions}
                      style={{ width: '100%', marginTop: 6 }}
                    />
                  </Form.Item>
                )}
                {(!canViewAllOrganizations || isMainOrg || isPartnerOrg) && (parentOrgData ? <Tag> {parentOrgData?.displayName}</Tag> : '-')}
              </span>
            </Form.Item>
          </Col>
          <Col lg={12} xs={24}>
            <Form.Item label={t('CreatedOn')}>
              <span>
                {moment.utc(organizationState.item.value?.createdOn).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}
              </span>
            </Form.Item>
          </Col>
        </Row>
        <>
          {!isPartnerOrg && !canDeleteOrg && (
            <>
              <Form.Item name={'testingEndDate'} hidden initialValue={moment(organizationState.item.value?.testingEndDate)} />
            </>
          )}
          {organizationState.item.value?.isTestingOrg && !canDeleteOrg && (
            <Row style={{ marginBottom: 10 }}>
              <Col span={24} style={{ color: token.colorError }}>{`${t('TestOrganization')}`}</Col>
              <Col span={24}>
                <span>{`${t('RemovalDate')}: ${moment.utc(organizationState.item.value?.testingEndDate).local().format(getDateFormat())}`}</span>
                <Form.Item name={'testingEndDate'} hidden initialValue={moment(organizationState.item.value?.testingEndDate)} />
              </Col>
            </Row>
          )}
          {canDeleteOrg && !isPartnerOrg && parentOrgData?.isTestingOrg && (
            <>
              <Col span={24} style={{ color: token.colorError }}>{`${t('TestOrganization')}`}</Col>
              <Col sm={13} xs={24} style={{ marginTop: 10, paddingRight: 5 }}>
                <span>{t('RemovalDate')}</span>
              </Col>
              <Col sm={11} xs={24} style={{ marginBottom: 20 }}>
                <Form.Item
                  initialValue={
                    organizationState.item.value?.testingEndDate
                      ? moment(organizationState.item.value?.testingEndDate)
                      : getDefaultOrganizationTestingEndDate()
                  }
                  name={nameof<OrganizationItem>('testingEndDate')}
                  rules={[() => ({ required: true })]}
                  noStyle
                >
                  <DatePicker allowClear={false} style={{ width: '100%' }} format="DD/MM/YYYY" maxDate={moment(parentOrgData.testingEndDate)} />
                </Form.Item>
              </Col>
            </>
          )}
          {canDeleteOrg &&
            !isMainOrg &&
            allChildrenAreTestingOrg &&
            !parentOrgData?.isTestingOrg &&
            ((!isPartnerOrg && !parentOrgData?.isTestingOrg) || isPartnerOrg || (!isPartnerOrg && !parentOrgData?.id)) && (
              <>
                <Col span={24}>
                  <Form.Item valuePropName="checked">
                    <Checkbox
                      checked={isTestOrg}
                      onChange={(e) => {
                        preventRefresh.current = true;
                        setIsTestOrg(() => e.target.checked);
                        form.setFieldValue('isTestingOrg', e.target.checked);
                        loadParentOptions(e.target.checked);
                      }}
                    >
                      {t('IsTestOrganization')}
                    </Checkbox>
                  </Form.Item>
                </Col>
                {!isTestOrg && <Form.Item name={'testingEndDate'} hidden initialValue={moment(organizationState.item.value?.testingEndDate)} />}
                {isTestOrg && (
                  <>
                    <Col sm={13} xs={24} style={{ marginTop: 10, paddingRight: 5 }}>
                      <span>{t('RemovalDate')}</span>
                    </Col>
                    <Col sm={11} xs={24} style={{ marginBottom: 20 }}>
                      <Form.Item
                        initialValue={
                          organizationState.item.value?.testingEndDate
                            ? moment(organizationState.item.value?.testingEndDate)
                            : getDefaultOrganizationTestingEndDate(minChildrenDeleteDate)
                        }
                        name={nameof<OrganizationItem>('testingEndDate')}
                        rules={[() => ({ required: true })]}
                        noStyle
                      >
                        <DatePicker allowClear={false} style={{ width: '100%' }} format="DD/MM/YYYY" minDate={minChildrenDeleteDate} />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </>
            )}

          {isPartnerOrg && !allChildrenAreTestingOrg && (
            <Col span={24}>
              <Form.Item>
                <Tooltip title={t('NotAllChildrenAreTestOrganizations')}>
                  <Checkbox disabled checked={false}>
                    {t('IsTestOrganization')}
                  </Checkbox>
                </Tooltip>
              </Form.Item>
            </Col>
          )}
        </>
        {(canEditOrg || canBlockOrg || canEnsureDb) && (
          <Form.Item style={{ marginBottom: 0 }}>
            {canEditOrg && (
              <>
                <Row gutter={[16, 8]}>
                  <Col xxl={12} xl={24} lg={24} sm={12} xs={24} style={{ minWidth: 80 }}>
                    <Button htmlType="button" type="default" disabled={organizationState.isBusy.value} onClick={onReset} style={{ width: '100%' }}>
                      {t('Reset')}
                    </Button>
                  </Col>
                  <Col xxl={12} xl={24} lg={24} sm={12} xs={24} style={{ minWidth: 130 }}>
                    <Button
                      type="primary"
                      disabled={organizationState.isBusy.value}
                      onClick={() => {
                        if (isTestOrg && !organizationState.item.value.isTestingOrg) {
                          modal.confirm({
                            title: t('TestOrgConfirmationMessage'),
                            onOk: () => onSave(),
                          });
                        } else onSave();
                      }}
                      style={{ width: '100%' }}
                    >
                      {t('SaveChanges')}
                    </Button>
                  </Col>
                </Row>
                <Divider />
              </>
            )}
            <Row gutter={[16, 8]}>
              {canBlockOrg && (
                <Col xxl={12} xl={24} xs={24} style={{ minWidth: 140 }}>
                  <Button
                    type="default"
                    danger={!organizationState.item.value?.isBlocked}
                    disabled={organizationState.isBusy.value}
                    onClick={onChangeBlockStatus}
                    icon={!organizationState.item.value?.isBlocked ? <LockFilled /> : <UnlockFilled />}
                    style={{ width: '100%' }}
                  >
                    {organizationState.item.value?.isBlocked ? t('Unblock') : t('Block')}
                  </Button>
                </Col>
              )}
              {canEnsureDb && (
                <Col lg={12} xs={24} style={{ minWidth: 140 }}>
                  <Button
                    type="primary"
                    disabled={organizationState.isBusy.value}
                    onClick={onEnqueueJob}
                    icon={<DatabaseOutlined />}
                    style={{ width: '100%' }}
                  >
                    {t('Restart Creation')}
                  </Button>
                </Col>
              )}
            </Row>
            {canManageAdminOrg && (
              <>
                <Divider />
                <div style={{ marginBottom: 4 }}>
                  <Text strong>{t('MFAStatus')}: </Text>
                  {mfaEnabled && (
                    <>
                      <CheckOutlined style={{ color: token.colorSuccess, marginLeft: 4, marginRight: 4 }} />
                      <Text style={{ color: token.colorSuccess }}>{t('Enabled')}</Text>
                    </>
                  )}
                  {!mfaEnabled && (
                    <>
                      <CloseOutlined style={{ color: token.colorError, marginLeft: 4, marginRight: 4 }} />
                      <Text style={{ color: token.colorError }}>{t('Disabled')}</Text>
                    </>
                  )}
                </div>
                <Col span={24}>
                  <Button
                    type="default"
                    disabled={organizationState.isBusy.value}
                    onClick={async () => {
                      const result = await organizationStore.switchMFA(organizationId, !mfaEnabled);
                      if (result?.isSuccess) {
                        organizationState.set((s) => {
                          s.item.twoFactorEnabled = !mfaEnabled;
                          return s;
                        });
                        setMfaEnabled((prev) => !prev);
                      }
                    }}
                    icon={<LoginOutlined />}
                    style={{ width: '100%', height: 'auto', minHeight: '60px', textWrap: 'wrap' }}
                  >
                    {mfaEnabled ? t('DisableMFA') : t('EnableMFA')}
                  </Button>
                </Col>
              </>
            )}
            <Divider />
            <Row gutter={[8, 8]} className="organization-option-buttons">
              {canManageAdminOrg && (
                <Col xxl={12} xl={24} lg={24} sm={24} xs={24} style={{ minWidth: 80 }}>
                  <Button
                    htmlType="button"
                    type="default"
                    disabled={organizationState.isBusy.value}
                    onClick={onCheckOrganizationDatabase}
                    icon={<DatabaseFilled />}
                    style={{ width: '100%' }}
                  >
                    {t('CheckDB')}
                  </Button>
                </Col>
              )}

              {organizationState?.item?.value?.creationStatus === OrganizationCreationStatus.Available && (
                <Col xxl={12} xl={24} lg={24} sm={24} xs={24} style={{ minWidth: 160 }}>
                  <Button
                    type="default"
                    htmlType="button"
                    disabled={organizationState.isBusy.value || fixInconsistenciesDisabled}
                    onClick={onFixInconsistencies}
                    icon={<BugFilled />}
                    style={{ width: '100%' }}
                  >
                    {t('FixInconsistencies')}
                  </Button>
                </Col>
              )}
              {canManageAdminOrg && (
                <Col span={24} style={{ minWidth: 140 }}>
                  <Button
                    className={isExporting ? 'export-organization-button' : ''}
                    type="default"
                    htmlType="button"
                    disabled={organizationState.isBusy.value || isExporting}
                    onClick={onExport}
                    style={{ width: '100%', minHeight: 56, height: 'fit-content' }}
                  >
                    {!isExporting && <DownloadOutlined />}
                    {isExporting && (
                      <Spin
                        spinning={isExporting}
                        indicator={loadingIcon}
                        size="small"
                        style={{ paddingLeft: '10px' }}
                        className="exporting-spinner"
                      />
                    )}
                    <span style={{ padding: '30' }}>{isExporting ? t('OrganizationIsExporting') : t('ExportOrganization')}</span>
                  </Button>
                </Col>
              )}
            </Row>
          </Form.Item>
        )}
      </Form>
    </Card>
  );
};

export default OrganizationGeneralPropertyEditCard;
